import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  serviceCheckoutAttemptStatusId,
  type ServiceCheckoutAttemptStatusId,
} from './ServiceCheckoutAttemptStatus.js'
import { serviceContractId, type ServiceContractId } from './ServiceContract.js'
import {
  serviceContractPaymentId,
  type ServiceContractPaymentId,
} from './ServiceContractPayment.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.service_checkout_attempt_ */
export type ServiceCheckoutAttemptId = string & {
  __brand: 'ServiceCheckoutAttemptId'
}

/** Represents the table public.service_checkout_attempt_ */
export default interface ServiceCheckoutAttemptTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ServiceCheckoutAttemptId,
    ServiceCheckoutAttemptId | undefined,
    ServiceCheckoutAttemptId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  serviceContractId: ColumnType<
    ServiceContractId,
    ServiceContractId,
    ServiceContractId
  >

  /** Database type: pg_catalog.int4 */
  amountInCents: ColumnType<number, number, number>

  /** Database type: pg_catalog.text */
  currency: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  status: ColumnType<
    ServiceCheckoutAttemptStatusId,
    ServiceCheckoutAttemptStatusId | undefined,
    ServiceCheckoutAttemptStatusId
  >

  /** Database type: pg_catalog.text */
  idempotencyKey: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  stripeRequest: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  sessionExpiresAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  deadlineAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.text */
  checkoutSessionId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  stripePaymentIntentId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.uuid */
  serviceContractPaymentId: ColumnType<
    ServiceContractPaymentId | null,
    ServiceContractPaymentId | null,
    ServiceContractPaymentId | null
  >

  /** Database type: pg_catalog.timestamptz */
  statusChangedAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.text */
  statusReason: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  statusEventId: ColumnType<string | null, string | null, string | null>
}

export type ServiceCheckoutAttempt = Selectable<ServiceCheckoutAttemptTable>

export type ServiceCheckoutAttemptInitializer =
  Insertable<ServiceCheckoutAttemptTable>

export type ServiceCheckoutAttemptMutator =
  Updateable<ServiceCheckoutAttemptTable>

export const serviceCheckoutAttemptId =
  z.string() as unknown as z.Schema<ServiceCheckoutAttemptId>

export const serviceCheckoutAttempt = z.object({
  id: serviceCheckoutAttemptId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  serviceContractId: serviceContractId,
  amountInCents: z.number(),
  currency: z.string(),
  status: serviceCheckoutAttemptStatusId,
  idempotencyKey: z.string(),
  stripeRequest: z.string(),
  sessionExpiresAt: z.coerce.date(),
  deadlineAt: z.coerce.date().nullable().optional(),
  checkoutSessionId: z.string().nullable().optional(),
  stripePaymentIntentId: z.string().nullable().optional(),
  serviceContractPaymentId: serviceContractPaymentId.nullable().optional(),
  statusChangedAt: z.coerce.date(),
  statusReason: z.string().nullable().optional(),
  statusEventId: z.string().nullable().optional(),
}) as unknown as z.Schema<ServiceCheckoutAttempt>

export const serviceCheckoutAttemptInitializer = z.object({
  id: serviceCheckoutAttemptId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  serviceContractId: serviceContractId,
  amountInCents: z.number(),
  currency: z.string(),
  status: serviceCheckoutAttemptStatusId.optional(),
  idempotencyKey: z.string(),
  stripeRequest: z.string(),
  sessionExpiresAt: z.coerce.date(),
  deadlineAt: z.coerce.date().optional().nullable(),
  checkoutSessionId: z.string().optional().nullable(),
  stripePaymentIntentId: z.string().optional().nullable(),
  serviceContractPaymentId: serviceContractPaymentId.optional().nullable(),
  statusChangedAt: z.coerce.date().optional(),
  statusReason: z.string().optional().nullable(),
  statusEventId: z.string().optional().nullable(),
}) as unknown as z.Schema<ServiceCheckoutAttemptInitializer>

export const serviceCheckoutAttemptMutator = z.object({
  id: serviceCheckoutAttemptId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  serviceContractId: serviceContractId.optional(),
  amountInCents: z.number().optional(),
  currency: z.string().optional(),
  status: serviceCheckoutAttemptStatusId.optional(),
  idempotencyKey: z.string().optional(),
  stripeRequest: z.string().optional(),
  sessionExpiresAt: z.coerce.date().optional(),
  deadlineAt: z.coerce.date().optional().nullable(),
  checkoutSessionId: z.string().optional().nullable(),
  stripePaymentIntentId: z.string().optional().nullable(),
  serviceContractPaymentId: serviceContractPaymentId.optional().nullable(),
  statusChangedAt: z.coerce.date().optional(),
  statusReason: z.string().optional().nullable(),
  statusEventId: z.string().optional().nullable(),
}) as unknown as z.Schema<ServiceCheckoutAttemptMutator>
